/**
 * Hero slider — crossfades between .hero__slide images.
 * Controls:
 *   • Thumbnails (.hero__thumb) — click to jump, active scrolls into view
 *   • Prev/next chevron buttons ([data-slider-prev/next]) — step through
 *   • Arrow keys when the strip is focused
 * Auto-advances every 4.5s; pauses on hover / hidden tab. Honors
 * prefers-reduced-motion.
 */
(() => {
    const strip = document.querySelector('[data-slider="hero"]');
    if (!strip) return;
    const wrapper = strip.closest(".hero") || strip;
    const track = strip.querySelector("[data-slider-track]");
    const slides = Array.from(wrapper.querySelectorAll(".hero__slide"));
    const thumbs = Array.from(strip.querySelectorAll(".hero__thumb"));
    const prevBtn = wrapper.querySelector("[data-slider-prev]");
    const nextBtn = wrapper.querySelector("[data-slider-next]");
    if (slides.length < 2 || thumbs.length < 2) return;

    const VISIBLE = 4;
    // Pixel width consumed by one thumb slot (thumb + gap). Read once at init.
    const thumbW = thumbs[0].getBoundingClientRect().width;
    const gap =
        parseFloat(getComputedStyle(track).gap) ||
        parseFloat(
            getComputedStyle(track).getPropertyValue("column-gap"),
        ) ||
        8;
    const slot = thumbW + gap;

    let active = Math.max(
        0,
        slides.findIndex((s) => s.classList.contains("hero__slide--active")),
    );
    if (active < 0) active = 0;

    const setActive = (i) => {
        const n = slides.length;
        active = ((i % n) + n) % n;
        slides.forEach((s, idx) =>
            s.classList.toggle("hero__slide--active", idx === active),
        );
        thumbs.forEach((t, idx) => {
            const on = idx === active;
            t.classList.toggle("hero__thumb--active", on);
            t.setAttribute("aria-selected", on ? "true" : "false");
        });
        const halfWindow = Math.floor(VISIBLE / 2);
        const maxStart = Math.max(0, n - VISIBLE);
        const windowStart = Math.min(
            Math.max(0, active - halfWindow),
            maxStart,
        );
        track.style.setProperty("--thumb-shift", `${windowStart * slot}px`);

        if (prevBtn) prevBtn.disabled = active === 0;
        if (nextBtn) nextBtn.disabled = active === n - 1;
    };

    const advance = (delta = 1) => setActive(active + delta);

    thumbs.forEach((t) => {
        t.addEventListener("click", () => {
            const idx = Number(t.dataset.index);
            if (!Number.isFinite(idx)) return;
            setActive(idx);
        });
    });

    prevBtn?.addEventListener("click", () => advance(-1));
    nextBtn?.addEventListener("click", () => advance(1));

    strip.addEventListener("keydown", (e) => {
        if (e.key === "ArrowRight") {
            advance(1);
            thumbs[active].focus();
        } else if (e.key === "ArrowLeft") {
            advance(-1);
            thumbs[active].focus();
        }
    });

    setActive(active);
})();

/**
 * Science section — 4-tab tablist with detail panel below + Clinical
 * References dialog opened from the panel CTA.
 * Tab data + reference content sourced from the angle-1 arrays.
 */
(() => {
    const section = document.querySelector("[data-science]");
    if (!section) return;

    const tabs = Array.from(section.querySelectorAll(".sci-tab"));
    const label = section.querySelector("[data-science-label]");
    const title = section.querySelector("[data-science-title]");
    const body = section.querySelector("[data-science-body]");
    if (!tabs.length || !label || !title || !body) return;

    const TABS = [
        {
            label: "01 · Inflammation",
            title: "Helps calm chronic inflammation",
            body: "Clinical research shows light therapy helps reduce inflammatory signaling in tissue — supporting a healthier environment.",
        },
        {
            label: "02 · Collagen Health",
            title: "Supports collagen health and tissue strength",
            body: "Red and near-infrared wavelengths are associated with collagen-supportive activity, helping gum tissue stay resilient over time.",
        },
        {
            label: "03 · Blood Flow",
            title: "Encourages healthier microcirculation",
            body: "Photobiomodulation can support local blood flow to deliver oxygen and nutrients needed for gum maintenance and recovery.",
        },
        {
            label: "04 · Cellular Energy",
            title: "Supports cellular energy production",
            body: "Light energy helps support mitochondrial function, improving the cell-level energy needed for repair and long-term gum stability.",
        },
    ];

    const REFERENCES = [
        {
            title: "01 — Inflammation",
            subtitle: "How Red & Infrared Light Supports Inflammatory Balance",
            sectionTitle: "What the Research Shows",
            subsectionTitle: "",
            bodyHtml:
                "Photobiomodulation (PBM) has been shown to influence key inflammatory signaling pathways in oral tissues. Red and near-infrared wavelengths interact with cells to help regulate pro-inflammatory cytokines and oxidative stress markers — both of which play a role in gum tissue breakdown.<br><br>In periodontal research, adjunctive light therapy has been associated with reduced bleeding, improved clinical attachment levels, and healthier tissue response when combined with standard care.<br><br><strong>Clinical References</strong><br><br><strong>Lee et al., 2017 – Lasers in Medical Science</strong><br>Low-level laser therapy demonstrated anti-inflammatory effects in periodontal ligament cells and reduced inflammatory mediator expression.<br><br><strong>Qadri et al., 2005 – Journal of Clinical Periodontology</strong><br>Adjunct low-level laser therapy reduced gingival inflammation and bleeding.<br><br><strong>Hamblin, 2017 – Photochemistry and Photobiology</strong><br>Review describing PBM's ability to downregulate NF-κB and inflammatory cytokines.",
            figure: null,
        },
        {
            title: "02 — Collagen Health",
            subtitle: "Supporting Structural Integrity of Gum Tissue",
            sectionTitle: "What the Research Shows",
            subsectionTitle: "",
            bodyHtml:
                "Collagen is the primary structural protein that anchors teeth within gum tissue. Red and near-infrared light therapy has been shown to stimulate fibroblast activity — the cells responsible for collagen production and extracellular matrix maintenance.<br><br>Laboratory and clinical studies report increased collagen synthesis and improved soft tissue repair responses following photobiomodulation.<br><br><strong>Clinical References</strong><br><br><strong>Kreisler et al., 2003 – Journal of Clinical Periodontology</strong><br>Increased gingival fibroblast proliferation after laser exposure.<br><br><strong>Pourzarandian et al., 2005 – Journal of Periodontal Research</strong><br>Enhanced collagen synthesis in gingival fibroblasts following PBM.<br><br><strong>Pinheiro &amp; Gerbi, 2006 – Photomedicine and Laser Surgery</strong><br>Review of PBM's role in connective tissue regeneration.",
            figure: null,
        },
        {
            title: "03 — Blood Flow",
            subtitle: "Enhancing Microcirculation in Gum Tissue",
            sectionTitle: "What the Research Shows",
            subsectionTitle: "",
            bodyHtml:
                "Red and near-infrared light therapy has been shown to increase local microcirculation and promote nitric oxide release, which plays a role in vascular dilation.<br><br>Improved blood flow supports oxygen delivery and nutrient transport — both essential for tissue maintenance and recovery. In periodontal contexts, PBM has been associated with improved wound healing responses and angiogenesis.<br><br><strong>Clinical References</strong><br><br><strong>Karu, 1999 – Journal of Photochemistry and Photobiology B</strong><br>Mechanisms linking PBM to nitric oxide release and circulation effects.<br><br><strong>Romanenko et al., 2025 – Lasers in Medical Science</strong><br>Laser Doppler measurements showed increased gingival microcirculation following photobiomodulation.",
            figure: null,
        },
        {
            title: "04 — Cellular Energy",
            subtitle: "Stimulating Mitochondrial Activity",
            sectionTitle: "What the Research Shows",
            subsectionTitle: "",
            bodyHtml:
                "Red and near-infrared wavelengths are absorbed by cytochrome c oxidase within mitochondria — the energy centers of cells. This interaction has been shown to increase ATP production.<br><br>Higher cellular energy availability supports tissue repair processes, cellular turnover, and biological resilience.<br><br><strong>Clinical References</strong><br><br><strong>Wong-Riley et al., 2005 – Journal of Biological Chemistry</strong><br>Demonstrated increased ATP production following near-infrared exposure.<br><br><strong>Hamblin, 2016 – Photomedicine and Laser Surgery</strong><br>Review outlining mitochondrial activation mechanisms in PBM.",
            figure: null,
        },
    ];

    // Track the active science index so the dialog can pull the matching reference
    let activeIndex = 0;

    const setActive = (i) => {
        const data = TABS[i];
        if (!data) return;
        activeIndex = i;
        tabs.forEach((tab, idx) => {
            const on = idx === i;
            tab.classList.toggle("is-active", on);
            tab.setAttribute("aria-selected", on ? "true" : "false");
        });
        label.textContent = data.label;
        title.textContent = data.title;
        body.textContent = data.body;
    };

    tabs.forEach((tab) => {
        tab.addEventListener("click", () => {
            const idx = Number(tab.dataset.scienceTab);
            if (!Number.isFinite(idx)) return;
            setActive(idx);
        });
    });

    const initial = Math.max(
        0,
        tabs.findIndex((t) => t.classList.contains("is-active")),
    );
    setActive(initial);

    // === Clinical references dialog ===
    const dialog = document.getElementById("sci-ref-dialog");
    if (!dialog) return;
    const panel = dialog.querySelector(".sci-ref__panel");
    const openers = Array.from(document.querySelectorAll("[data-sci-ref-open]"));
    const closers = Array.from(dialog.querySelectorAll("[data-sci-ref-close]"));
    const dTitle = document.getElementById("sci-ref-title");
    const dSubtitle = document.getElementById("sci-ref-subtitle");
    const dSection = document.getElementById("sci-ref-section-title");
    const dSubsection = document.getElementById("sci-ref-subsection-title");
    const dBody = document.getElementById("sci-ref-body");
    const dFigure = document.getElementById("sci-ref-figure");
    const dFigureImg = document.getElementById("sci-ref-figure-image");
    const dFigureCaption = document.getElementById("sci-ref-figure-caption");
    let lastTrigger = null;

    const populate = (i) => {
        const ref = REFERENCES[Math.max(0, Math.min(REFERENCES.length - 1, i))];
        if (!ref) return;
        if (dTitle) dTitle.textContent = ref.title;
        if (dSubtitle) dSubtitle.textContent = ref.subtitle;
        if (dSection) dSection.textContent = ref.sectionTitle;
        if (dSubsection) dSubsection.textContent = ref.subsectionTitle || "";
        if (dBody) dBody.innerHTML = ref.bodyHtml;
        if (dFigure && dFigureImg && dFigureCaption) {
            if (ref.figure && ref.figure.src) {
                dFigure.hidden = false;
                dFigureImg.setAttribute("src", ref.figure.src);
                dFigureImg.setAttribute("alt", ref.figure.alt || "");
                dFigureCaption.innerHTML = ref.figure.captionHtml || "";
            } else {
                dFigure.hidden = true;
                dFigureImg.removeAttribute("src");
                dFigureImg.setAttribute("alt", "");
                dFigureCaption.textContent = "";
            }
        }
    };

    const setDialog = (open) => {
        document.body.classList.toggle("sci-ref-open", open);
        dialog.classList.toggle("is-open", open);
        dialog.setAttribute("aria-hidden", String(!open));
        if (open) {
            populate(activeIndex);
            setTimeout(() => panel && panel.focus(), 0);
        } else if (lastTrigger) {
            lastTrigger.focus();
        }
    };

    openers.forEach((btn) => {
        btn.addEventListener("click", () => {
            lastTrigger = btn;
            setDialog(true);
        });
    });
    closers.forEach((btn) => {
        btn.addEventListener("click", () => setDialog(false));
    });
    // Esc closes
    document.addEventListener("keydown", (e) => {
        if (e.key === "Escape" && dialog.classList.contains("is-open")) {
            setDialog(false);
        }
    });
})();

/**
 * What to Expect tabs — 3 timeline tabs sharing one content card.
 * Data sourced from angle-1 EXPECTATIONS array.
 */
(() => {
    const section = document.querySelector("[data-expect]");
    if (!section) return;
    const tabs = Array.from(section.querySelectorAll(".expect-tab"));
    const title = section.querySelector("[data-expect-title]");
    const copy = section.querySelector("[data-expect-copy]");
    const bullets = Array.from(section.querySelectorAll("[data-expect-bullet]"));
    if (!tabs.length || !title || !copy) return;

    const DATA = [
        {
            title: "First Use",
            body: "Most people notice a gentle warmth, with gums often feeling calmer and less sensitive afterward. Some users also notice reduced tenderness and slightly darker gum color as circulation increases.",
            bullets: [
                "Feeling more in control of gum health",
                "Greater comfort during brushing and eating",
            ],
        },
        {
            title: "1-3 Months",
            body: "With consistent use, many users report less bleeding while brushing, fewer irritation flare-ups, and a more stable daily routine for gum support.",
            bullets: [
                "Noticeably calmer gum response day to day",
                "More confidence during cleanings and checkups",
            ],
        },
        {
            title: "Long Term",
            body: "Long-term users often describe stronger maintenance between visits and better support for keeping natural teeth stable as they age.",
            bullets: [
                "Sustained support for healthier gum tissue",
                "Built as a practical daily prevention habit",
            ],
        },
    ];

    const setActive = (i) => {
        const idx = Math.max(0, Math.min(DATA.length - 1, i));
        const item = DATA[idx];
        title.textContent = item.title;
        copy.textContent = item.body;
        bullets.forEach((b, k) => {
            b.textContent = item.bullets[k] || "";
        });
        tabs.forEach((tab, k) => {
            const on = k === idx;
            tab.classList.toggle("is-active", on);
            tab.setAttribute("aria-selected", on ? "true" : "false");
        });
    };

    tabs.forEach((tab) => {
        tab.addEventListener("click", () => {
            const idx = Number(tab.dataset.expectTab);
            if (!Number.isFinite(idx)) return;
            setActive(idx);
        });
    });

    const initial = Math.max(
        0,
        tabs.findIndex((t) => t.classList.contains("is-active")),
    );
    setActive(initial);
})();

/**
 * How It Works accordion — only one step open at a time, animated height
 * via the --accordion-content-height custom property (matches angle-1 pattern).
 */
(() => {
    const steps = Array.from(document.querySelectorAll(".how-step"));
    if (!steps.length) return;

    const syncHeights = () => {
        steps.forEach((step) => {
            const body = step.querySelector(".how-step__body");
            if (!body) return;
            // Measure the natural body height. Temporarily clear our height
            // constraint to read scrollHeight accurately.
            const prevHeight = body.style.height;
            body.style.height = "auto";
            const h = Math.ceil(body.scrollHeight);
            body.style.height = prevHeight;
            body.style.setProperty(
                "--accordion-content-height",
                `${h}px`,
            );
        });
    };

    const openStep = (target) => {
        steps.forEach((step) => {
            const isTarget = step === target;
            const btn = step.querySelector(".how-step__toggle");
            step.classList.toggle("is-open", isTarget);
            if (btn) btn.setAttribute("aria-expanded", String(isTarget));
        });
    };

    // Init: ensure exactly one open step, measure heights, wire clicks
    const initial =
        steps.find((s) => s.classList.contains("is-open")) || steps[0];
    openStep(initial);
    syncHeights();

    steps.forEach((step) => {
        const btn = step.querySelector(".how-step__toggle");
        if (!btn) return;
        btn.addEventListener("click", () => {
            if (step.classList.contains("is-open")) return; // single-open accordion
            syncHeights();
            openStep(step);
        });
    });

    // Recompute when the layout changes (viewport resize, font load, etc.)
    let raf = 0;
    const schedule = () => {
        if (raf) cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => {
            syncHeights();
            raf = 0;
        });
    };
    window.addEventListener("resize", schedule);
    window.addEventListener("load", schedule);
})();

/**
 * Dentists carousel — same scroll pattern as hr-customers.js (step = card-to-card
 * delta, prev/next disabled at edges, progress fill follows scrollLeft / max).
 * Adds video play/pause ported from angle-1/landing: click a play button to
 * swap poster for inline <video>; playing one stops all the others.
 */
(() => {
    const section = document.querySelector(".rlt-dentists");
    if (!section) return;
    const track = section.querySelector(".rlt-dentists__track");
    const cards = track && Array.from(track.querySelectorAll(".rlt-dentist-card"));
    const prev = section.querySelector(".icon-btn--prev");
    const next = section.querySelector(".icon-btn--next");
    const fill = section.querySelector(".rlt-dentists__progress-fill");
    if (!track || !cards || !cards.length || !prev || !next) return;

    const step = () => {
        if (cards.length < 2) return cards[0].offsetWidth;
        return (
            cards[1].getBoundingClientRect().left -
            cards[0].getBoundingClientRect().left
        );
    };

    const update = () => {
        const max = track.scrollWidth - track.clientWidth;
        const x = track.scrollLeft;
        prev.disabled = x <= 1;
        next.disabled = x >= max - 1;
        if (fill) {
            const pct =
                track.scrollWidth > 0
                    ? Math.min(
                          100,
                          ((x + track.clientWidth) / track.scrollWidth) * 100,
                      )
                    : 100;
            fill.style.width = pct + "%";
        }
    };

    prev.addEventListener("click", () =>
        track.scrollBy({ left: -step(), behavior: "smooth" }),
    );
    next.addEventListener("click", () =>
        track.scrollBy({ left: step(), behavior: "smooth" }),
    );
    track.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    update();

    /* video play/pause — mutually exclusive across cards */
    const syncTrackState = () => {
        const hasPlaying = cards.some((c) =>
            c.classList.contains("is-playing"),
        );
        track.classList.toggle("has-playing", hasPlaying);
    };

    const stopCard = (card) => {
        const video = card.querySelector(".rlt-dentist-card__video");
        if (video) {
            video.pause();
            if (!video.ended && video.currentTime > 0) video.currentTime = 0;
        }
        card.classList.remove("is-playing");
    };

    cards.forEach((card) => {
        const media = card.querySelector(".rlt-dentist-card__media");
        const play = card.querySelector(".rlt-dentist-card__play");
        const video = card.querySelector(".rlt-dentist-card__video");
        const src = media ? media.getAttribute("data-video-src") : "";
        if (!media || !play || !video || !src) return;

        play.addEventListener("click", () => {
            if (card.classList.contains("is-playing")) {
                stopCard(card);
                syncTrackState();
                return;
            }
            cards.forEach((other) => {
                if (other !== card) stopCard(other);
            });
            if (!video.getAttribute("src")) video.setAttribute("src", src);
            card.classList.add("is-playing");
            syncTrackState();
            const p = video.play();
            if (p && typeof p.catch === "function") p.catch(() => {});
        });

        video.addEventListener("ended", () => {
            stopCard(card);
            syncTrackState();
        });
    });
})();

/**
 * Testimonials carousel — same scroll + video-play pattern as the dentists
 * carousel above. Ported from angle-1/landing social-proof block: click play
 * → swap image/shade/chips for inline <video>, mutually exclusive across cards.
 */
(() => {
    const section = document.querySelector(".rlt-testimonials");
    if (!section) return;
    const track = section.querySelector(".rlt-testimonials__track");
    const cards = track && Array.from(track.querySelectorAll(".rlt-testimonial-card"));
    const prev = section.querySelector(".icon-btn--prev");
    const next = section.querySelector(".icon-btn--next");
    const fill = section.querySelector(".rlt-testimonials__progress-fill");
    if (!track || !cards || !cards.length || !prev || !next) return;

    const step = () => {
        if (cards.length < 2) return cards[0].offsetWidth;
        return (
            cards[1].getBoundingClientRect().left -
            cards[0].getBoundingClientRect().left
        );
    };

    const update = () => {
        const max = track.scrollWidth - track.clientWidth;
        const x = track.scrollLeft;
        prev.disabled = x <= 1;
        next.disabled = x >= max - 1;
        if (fill) {
            const pct =
                track.scrollWidth > 0
                    ? Math.min(
                          100,
                          ((x + track.clientWidth) / track.scrollWidth) * 100,
                      )
                    : 100;
            fill.style.width = pct + "%";
        }
    };

    prev.addEventListener("click", () =>
        track.scrollBy({ left: -step(), behavior: "smooth" }),
    );
    next.addEventListener("click", () =>
        track.scrollBy({ left: step(), behavior: "smooth" }),
    );
    track.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    update();

    /* video play/pause — mutually exclusive across cards */
    const stopCard = (card) => {
        const video = card.querySelector(".rlt-testimonial-card__video");
        if (video) {
            video.pause();
            if (!video.ended && video.currentTime > 0) video.currentTime = 0;
        }
        card.classList.remove("is-playing");
    };

    cards.forEach((card) => {
        const play = card.querySelector(".rlt-testimonial-card__play");
        const video = card.querySelector(".rlt-testimonial-card__video");
        const src = card.getAttribute("data-video-src") || "";
        if (!play || !video || !src) return;

        play.addEventListener("click", () => {
            if (card.classList.contains("is-playing")) {
                stopCard(card);
                return;
            }
            cards.forEach((other) => {
                if (other !== card) stopCard(other);
            });
            if (!video.getAttribute("src")) video.setAttribute("src", src);
            card.classList.add("is-playing");
            const p = video.play();
            if (p && typeof p.catch === "function") p.catch(() => {});
        });

        video.addEventListener("ended", () => {
            stopCard(card);
        });
    });
})();

/**
 * Price compare table — horizontal scroll on mobile/tablet. Step size matches
 * one body cell width (via data-scroll-step-selector). Same icon-btn +
 * progress-fill pattern as the dentists/testimonials sliders above.
 */
(() => {
    const section = document.querySelector(".rlt-compare");
    if (!section) return;
    const wrap = section.querySelector(".rlt-compare__table-wrap");
    const prev = section.querySelector(".icon-btn--prev");
    const next = section.querySelector(".icon-btn--next");
    const fill = section.querySelector(".rlt-compare__progress-fill");
    const controls = section.querySelector(".rlt-compare__controls");
    if (!wrap || !prev || !next) return;

    const step = () => {
        const sel = wrap.getAttribute("data-scroll-step-selector");
        const node = sel ? wrap.querySelector(sel) : null;
        if (node) {
            const w = node.getBoundingClientRect().width;
            if (w > 0) return w + 24; /* + table border-spacing */
        }
        return wrap.clientWidth * 0.6;
    };

    const update = () => {
        const max = wrap.scrollWidth - wrap.clientWidth;
        const canScroll = max > 4;
        if (controls) controls.classList.toggle("is-hidden", !canScroll);
        if (!canScroll) {
            if (fill) fill.style.width = "100%";
            prev.disabled = true;
            next.disabled = true;
            return;
        }
        const x = wrap.scrollLeft;
        prev.disabled = x <= 1;
        next.disabled = x >= max - 1;
        if (fill) {
            const pct = Math.min(
                100,
                ((x + wrap.clientWidth) / wrap.scrollWidth) * 100,
            );
            fill.style.width = pct + "%";
        }
    };

    prev.addEventListener("click", () =>
        wrap.scrollBy({ left: -step(), behavior: "smooth" }),
    );
    next.addEventListener("click", () =>
        wrap.scrollBy({ left: step(), behavior: "smooth" }),
    );
    wrap.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    window.addEventListener("load", update);
    update();
})();

/**
 * Complete-your-routine carousel — same scroll pattern as the dentists /
 * testimonials sliders: prev/next paginate by one card-width + gap, progress
 * fill follows scrollLeft / scrollWidth, controls auto-hide if no overflow.
 */
(() => {
    const section = document.querySelector(".rlt-prd");
    if (!section) return;
    const track = section.querySelector(".rlt-prd__track");
    const cards = track && Array.from(track.querySelectorAll(".rlt-prd-card"));
    const prev = section.querySelector(".icon-btn--prev");
    const next = section.querySelector(".icon-btn--next");
    const fill = section.querySelector(".rlt-prd__progress-fill");
    const controls = section.querySelector(".rlt-prd__controls");
    if (!track || !cards || !cards.length || !prev || !next) return;

    const step = () => {
        if (cards.length < 2) return cards[0].offsetWidth;
        return (
            cards[1].getBoundingClientRect().left -
            cards[0].getBoundingClientRect().left
        );
    };

    const update = () => {
        const max = track.scrollWidth - track.clientWidth;
        const canScroll = max > 4;
        if (controls) controls.classList.toggle("is-hidden", !canScroll);
        if (!canScroll) {
            if (fill) fill.style.width = "100%";
            prev.disabled = true;
            next.disabled = true;
            return;
        }
        const x = track.scrollLeft;
        prev.disabled = x <= 1;
        next.disabled = x >= max - 1;
        if (fill) {
            const pct = Math.min(
                100,
                ((x + track.clientWidth) / track.scrollWidth) * 100,
            );
            fill.style.width = pct + "%";
        }
    };

    prev.addEventListener("click", () =>
        track.scrollBy({ left: -step(), behavior: "smooth" }),
    );
    next.addEventListener("click", () =>
        track.scrollBy({ left: step(), behavior: "smooth" }),
    );
    track.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    window.addEventListener("load", update);
    update();
})();

/**
 * FAQ accordion — single-open. Click an item to open it (closing the rest).
 * Height animated via --accordion-content-height set from scrollHeight.
 */
(() => {
    const items = Array.from(document.querySelectorAll(".faq-item"));
    if (!items.length) return;

    const syncHeights = () => {
        items.forEach((item) => {
            const ans = item.querySelector(".faq-item__a");
            if (!ans) return;
            const prev = ans.style.height;
            ans.style.height = "auto";
            const h = Math.ceil(ans.scrollHeight);
            ans.style.height = prev;
            ans.style.setProperty("--accordion-content-height", `${h}px`);
        });
    };

    const openItem = (target) => {
        items.forEach((item) => {
            const btn = item.querySelector(".faq-item__toggle");
            const isTarget = item === target;
            item.classList.toggle("is-open", isTarget);
            if (btn) btn.setAttribute("aria-expanded", String(isTarget));
        });
    };

    const initial = items.find((i) => i.classList.contains("is-open")) || items[0];
    openItem(initial);
    syncHeights();

    items.forEach((item) => {
        const btn = item.querySelector(".faq-item__toggle");
        if (!btn) return;
        btn.addEventListener("click", () => {
            if (item.classList.contains("is-open")) return;
            syncHeights();
            openItem(item);
        });
    });

    let raf = 0;
    const schedule = () => {
        if (raf) cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => {
            syncHeights();
            raf = 0;
        });
    };
    window.addEventListener("resize", schedule);
    window.addEventListener("load", schedule);
})();
